/** Company profile and localization settings. */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LOCALES } from "@offertli/shared";
import { Button } from "../../components/ui/Button.js";
import { Field } from "../../components/ui/Field.js";
import { InputText as Input } from "@primereact/ui/inputtext";
import { Select } from "@primereact/ui/select";
import { ToggleSwitch as Toggle } from "@primereact/ui/toggleswitch";
import { apiGet, apiPatch, apiUpload } from "../../lib/api.js";
import { APP_CONFIG_QUERY_KEY } from "../../lib/app-config.js";
import { ErrorPanel, LoadingPanel, MutationError, Section } from "../shared.js";

export type Company = {
  name: string;
  tradingName?: string;
  addressLine1: string;
  addressLine2?: string;
  postalCode: string;
  city: string;
  canton?: string;
  country: string;
  email: string;
  phone?: string;
  website?: string;
  vatNumber?: string;
  commercialRegister?: string;
  useCompanyBranding: boolean;
  logoUrl?: string;
};

export type Localization = {
  defaultLocale: string;
  timezone: string;
};

export function CompanyForm() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["settings", "company"],
    queryFn: () =>
      apiGet<{
        data: Company;
      }>("/settings/company").then((response) => response.data),
  });
  const [form, setForm] = useState<Company | null>(null);
  useEffect(() => {
    if (query.data) setForm(query.data);
  }, [query.data]);
  const save = useMutation({
    mutationFn: () => apiPatch("/settings/company", form),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["settings", "company"],
        }),
        queryClient.invalidateQueries({
          queryKey: APP_CONFIG_QUERY_KEY,
        }),
      ]);
    },
  });
  const logo = useMutation({
    mutationFn: (file: File) => {
      const body = new FormData();
      body.append("file", file);
      return apiUpload("/settings/company/logo", body);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["settings", "company"],
        }),
        queryClient.invalidateQueries({
          queryKey: APP_CONFIG_QUERY_KEY,
        }),
      ]);
    },
  });
  if (query.isLoading || !form) return <LoadingPanel />;
  if (query.error) return <ErrorPanel error={query.error} />;
  const text = (key: keyof Company, value: string) =>
    setForm({
      ...form,
      [key]: value,
    });
  return (
    <Section
      title={t("settings.company", {
        defaultValue: "Company",
      })}
    >
      <form
        className="grid gap-4 sm:grid-cols-2"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <Field
          label={t("settings.logo", {
            defaultValue: "Logo",
          })}
          className="sm:col-span-2"
        >
          <div className="flex items-center gap-4">
            {form.logoUrl ? (
              <img
                src={form.logoUrl}
                alt=""
                className="h-16 w-32 object-contain"
              />
            ) : null}
            <Input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const file = event.target.files?.[0];
                if (file) logo.mutate(file);
              }}
            />
          </div>
        </Field>
        <Field
          label={t("settings.legalName", {
            defaultValue: "Legal name",
          })}
          required
        >
          <Input
            required
            value={form.name}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              text("name", event.target.value)
            }
          />
        </Field>
        <Field
          label={t("settings.tradingName", {
            defaultValue: "Trading name",
          })}
        >
          <Input
            value={form.tradingName ?? ""}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              text("tradingName", event.target.value)
            }
          />
        </Field>
        <Field label={t("common.email")} required>
          <Input
            type="email"
            required
            value={form.email}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              text("email", event.target.value)
            }
          />
        </Field>
        <Field
          label={t("common.phone", {
            defaultValue: "Phone",
          })}
        >
          <Input
            value={form.phone ?? ""}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              text("phone", event.target.value)
            }
          />
        </Field>
        <Field
          label={t("common.address", {
            defaultValue: "Address",
          })}
          required
        >
          <Input
            required
            value={form.addressLine1}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              text("addressLine1", event.target.value)
            }
          />
        </Field>
        <Field
          label={t("common.address2", {
            defaultValue: "Address line 2",
          })}
        >
          <Input
            value={form.addressLine2 ?? ""}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              text("addressLine2", event.target.value)
            }
          />
        </Field>
        <Field
          label={t("common.postalCode", {
            defaultValue: "Postal code",
          })}
          required
        >
          <Input
            required
            value={form.postalCode}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              text("postalCode", event.target.value)
            }
          />
        </Field>
        <Field
          label={t("common.city", {
            defaultValue: "City",
          })}
          required
        >
          <Input
            required
            value={form.city}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              text("city", event.target.value)
            }
          />
        </Field>
        <Field
          label={t("settings.vatNumber", {
            defaultValue: "VAT number",
          })}
        >
          <Input
            value={form.vatNumber ?? ""}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              text("vatNumber", event.target.value)
            }
          />
        </Field>
        <div className="sm:col-span-2 rounded-lg border border-app-border p-4">
          <div>
            <label
              className="flex items-center gap-3 text-sm font-medium"
              htmlFor="use-company-branding"
            >
              <Toggle.Root
                inputId="use-company-branding"
                checked={form.useCompanyBranding}
                onCheckedChange={(event: { checked: boolean }) =>
                  ((useCompanyBranding) =>
                    setForm({
                      ...form,
                      useCompanyBranding,
                    }))(event.checked)
                }
              >
                <Toggle.Control>
                  <Toggle.Handle></Toggle.Handle>
                </Toggle.Control>
              </Toggle.Root>
              <span>{t("settings.useCompanyBranding")}</span>
            </label>
            <small className="mt-1 block text-app-muted">
              {t("settings.useCompanyBrandingHint")}
            </small>
          </div>
        </div>
        <MutationError error={save.error || logo.error} />
        <div className="sm:col-span-2">
          <Button type="submit" loading={save.isPending || undefined}>
            {t("common.save")}
          </Button>
        </div>
      </form>
    </Section>
  );
}

export function LocalizationForm() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["settings", "localization"],
    queryFn: () =>
      apiGet<{
        data: Localization;
      }>("/settings/localization").then((response) => response.data),
  });
  const [form, setForm] = useState<Localization | null>(null);
  useEffect(() => {
    if (query.data) setForm(query.data);
  }, [query.data]);
  const save = useMutation({
    mutationFn: () => apiPatch("/settings/localization", form),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["settings", "localization"],
        }),
        queryClient.invalidateQueries({
          queryKey: APP_CONFIG_QUERY_KEY,
        }),
      ]);
    },
  });
  if (query.isLoading || !form) return <LoadingPanel />;
  if (query.error) return <ErrorPanel error={query.error} />;
  return (
    <Section
      title={t("settings.localization", {
        defaultValue: "Localization",
      })}
    >
      <form
        className="grid gap-4 sm:grid-cols-2"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <Field
          label={t("common.language", {
            defaultValue: "Language",
          })}
        >
          <Select.Root
            value={form.defaultLocale}
            onValueChange={(event: { value: unknown }) =>
              setForm({
                ...form,
                defaultLocale: event.value as string,
              })
            }
            fluid
            options={[
              ...SUPPORTED_LOCALES.map((locale) => ({
                value: locale,
                label: t(`locale.${locale}`),
              })),
            ]}
            optionLabel="label"
            optionValue="value"
          >
            <Select.Trigger type="button">
              <Select.Value />
              <Select.Indicator className="pi pi-chevron-down" />
            </Select.Trigger>
            <Select.Portal>
              <Select.Positioner>
                <Select.Popup>
                  <Select.List />
                </Select.Popup>
              </Select.Positioner>
            </Select.Portal>
          </Select.Root>
        </Field>
        <Field
          label={t("settings.timezone", {
            defaultValue: "Timezone",
          })}
        >
          <Input
            value={form.timezone}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setForm({
                ...form,
                timezone: event.target.value,
              })
            }
          />
        </Field>
        <MutationError error={save.error} />
        <div className="sm:col-span-2">
          <Button type="submit" loading={save.isPending || undefined}>
            {t("common.save")}
          </Button>
        </div>
      </form>
    </Section>
  );
}
